import React, { useState, useEffect } from 'react';
import { MessageSquare, Star, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import '../../styles/common/latte.css';
import '../../styles/common/latte-components.css';

// 라떼부장님 이미지 (안경 쓴 중년 남성)
import latteBossImg from '../../assets/images/latte-boss.png';
// 라떼매니저 이미지 (친절한 여성)
import latteManagerImg from '../../assets/images/latte-manager.png';

const ConsultantSelector = ({ onSelectConsultant, onCancel }) => {
  
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [hoveredConsultant, setHoveredConsultant] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [promptIds, setPromptIds] = useState({
    'latte-manager': 10,
    'latte-boss': 11
  });
  const [loading, setLoading] = useState(true);
  const [serviceError, setServiceError] = useState(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 활성화된 프롬프트 ID들을 가져오기
  useEffect(() => {
    const fetchPromptIds = async () => {
      
      // supabase가 없으면 문제
      if (!supabase) {
        setServiceError({
          title: 'Supabase 연결 오류',
          message: '데이터베이스 연결에 실패했습니다.',
          detail: 'Supabase 객체를 찾을 수 없습니다.'
        });
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        // 직접 DB에서 활성화된 프롬프트 조회
        const { data: activePrompts, error } = await supabase
          .from('system_prompts')
          .select('id, consultant_type')
          .eq('name', 'latteis_main')
          .eq('is_active', true)
          .in('target_user_group', ['all', 'admin']);
        
        
        if (error) {
          console.error('[ConsultantSelector] Supabase 에러:', error);
          throw error;
        }
        
        // consultant_type별로 ID 매핑
        const activePromptIds = {
          'latte-manager': null,
          'latte-boss': null
        };
        
        if (activePrompts && activePrompts.length > 0) {
          activePrompts.forEach(prompt => {
            if (prompt.consultant_type && activePromptIds.hasOwnProperty(prompt.consultant_type)) {
              activePromptIds[prompt.consultant_type] = prompt.id;
            }
          });
        }
        
        setPromptIds(activePromptIds);
        
        // 두 프롬프트 모두 없으면 에러 표시
        if (!activePromptIds['latte-manager'] && !activePromptIds['latte-boss']) {
          setServiceError({
            title: '서비스 점검 중',
            message: '현재 AI 컨설팅 서비스가 점검 중입니다.',
            detail: '시스템 프롬프트가 활성화되지 않았습니다. 관리자에게 문의해주세요.'
          });
        }
      } catch (error) {
        console.error('[ConsultantSelector] fetchPromptIds 에러:', error);
        console.error('[ConsultantSelector] 에러 상세:', {
          message: error.message,
          stack: error.stack,
          code: error.code
        });
        setServiceError({
          title: '시스템 오류',
          message: '서비스를 초기화하는 중 문제가 발생했습니다.',
          detail: '잠시 후 다시 시도해주세요. 문제가 계속되면 관리자에게 문의해주세요.'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchPromptIds();
  }, []); // onCancel 제거 - 무한 재렌더링 방지

  // 서비스 에러 모달
  if (serviceError) {
    return (
      <div className="modal-overlay" style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999 
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0.75rem',
          padding: '2rem',
          maxWidth: '28rem',
          width: '90%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}>
          {/* 에러 아이콘 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '1rem'
          }}>
            <div style={{
              width: '3rem',
              height: '3rem',
              borderRadius: '50%',
              backgroundColor: '#FEE2E2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 9V13M12 17H12.01M5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19Z" 
                  stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          
          {/* 제목 */}
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#111827',
            textAlign: 'center',
            marginBottom: '0.5rem'
          }}>
            {serviceError.title}
          </h2>
          
          {/* 메시지 */}
          <p style={{
            fontSize: '1rem',
            color: '#374151',
            textAlign: 'center',
            marginBottom: '0.75rem'
          }}>
            {serviceError.message}
          </p>
          
          {/* 상세 설명 */}
          <div style={{
            padding: '0.75rem',
            backgroundColor: '#F9FAFB',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{
              fontSize: '0.875rem',
              color: '#6B7280',
              textAlign: 'center'
            }}>
              {serviceError.detail}
            </p>
          </div>
          
          {/* 닫기 버튼 */}
          <button
            onClick={onCancel}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: '#EF4444',
              color: 'white',
              borderRadius: '0.5rem',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#DC2626'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#EF4444'}
          >
            닫기
          </button>
        </div>
      </div>
    );
  }

  const consultants = [
    {
      id: 'latte-boss',
      systemPromptId: promptIds['latte-boss'],
      name: '라떼부장님',
      title: '30년 경력의 꼰꼰한 라떼부장님',
      image: latteBossImg,
      color: 'orange',
      description: '호통과 칭찬을 오가는 30년차 인사 전문가의 애정 어린 직설 코칭',
      characteristics: [
        '"라떼는 말이야..."로 시작하는 30년 경력 노하우 대방출',
        '꼰꼰하게 디테일 하나도 놓치지 않음',
        '호통치다가도 잘하면 칭찬 아끼지 않음',
        '반말로 다가오는 친근한 선배 스타일'
      ],
      sampleMessage: "라떼는 말이야... 우리 땐 보고서에 점 하나 잘못 찍어도 불호령이 떨어졌어. 그래서 자네 행동이 가져온 결과가 구체적으로 뭐였냔 말이야! 숫자로 딱딱 증명해야 듣는 사람도 고개를 끄덕이는 거라고.",
      personality: "charismatic"
    },
    {
      id: 'latte-manager',
      systemPromptId: promptIds['latte-manager'],
      name: '라떼매니저',
      title: '트렌디하고 꼼꼼한 라떼매니저',
      image: latteManagerImg,
      color: 'blue',
      description: '세심하고 논리적인 분석으로 트렌디한 이력서를 완성하는 전문 컨설턴트',
      characteristics: [
        '최신 채용 트렌드를 반영한 날카로운 전략 제시',
        '세심하고 꼼꼼한 단계별 가이드',
        '논리적이고 체계적인 분석가',
        '존댓말로 친절하게 응원하는 소통 스타일'
      ],
      sampleMessage: "안녕하세요! 지금까지 말씀해주신 경험을 분석해보니, 특히 데이터 기반의 의사결정으로 비즈니스 임팩트를 만들어내는 강점이 돋보입니다. 이 부분을 더 구체적으로 표현해보시겠어요?",
      personality: "professional"
    }
  ];

  const handleSelectConsultant = (consultant) => {
    // systemPromptId가 없으면 선택할 수 없음
    if (!consultant.systemPromptId) {
      alert(`${consultant.name}에 대한 활성화된 프롬프트가 없습니다. 관리자에게 문의해주세요.`);
      return;
    }
    
    setSelectedConsultant(consultant);
    // 약간의 딜레이 후 선택 처리 (애니메이션 효과)
    setTimeout(() => {
      onSelectConsultant({
        systemPromptId: consultant.systemPromptId,
        consultantType: consultant.id,
        consultantName: consultant.name
      });
    }, 300);
  };

  // 모바일 스타일
  if (isMobile) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center'
      }}>
        <div style={{
          backgroundColor: 'white',
          width: '100%',
          maxWidth: '100%',
          maxHeight: '90vh',
          borderTopLeftRadius: '1.5rem',
          borderTopRightRadius: '1.5rem',
          boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden'
        }}>
          {/* Mobile Header */}
          <div style={{
            position: 'relative',
            padding: '1rem 1rem 1.5rem',
            borderBottom: '1px solid #E5E7EB'
          }}>
            {/* 드래그 핸들 */}
            <div style={{
              position: 'absolute',
              top: '0.5rem',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '2.5rem',
              height: '0.25rem',
              backgroundColor: '#D1D5DB',
              borderRadius: '9999px'
            }} />
            
            {/* 닫기 버튼 */}
            <button
              onClick={onCancel}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                width: '2rem',
                height: '2rem',
                borderRadius: '50%',
                backgroundColor: '#F3F4F6',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s'
              }}
              onTouchStart={(e) => e.currentTarget.style.backgroundColor = '#E5E7EB'}
              onTouchEnd={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
            >
              <X size={18} color="#6B7280" />
            </button>
            
            <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
              <h2 style={{ 
                fontSize: '1.125rem', 
                fontWeight: '700', 
                color: '#111827',
                marginBottom: '0.375rem' 
              }}>
                라떼이즈 컨설턴트를 선택해주세요
              </h2>
              <p style={{ 
                fontSize: '0.875rem', 
                color: '#6B7280' 
              }}>
                각 컨설턴트마다 고유한 코칭 스타일이 있습니다.
              </p>
            </div>
          </div>

          {/* Mobile Cards */}
          <div style={{ 
            padding: '1rem',
            overflowY: 'auto',
            maxHeight: 'calc(90vh - 120px)',
            paddingBottom: '2rem'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
              {consultants.map((consultant) => {
                const consultantImage = consultant.image;
                const isSelected = selectedConsultant?.id === consultant.id;
                
                return (
                  <button
                    key={consultant.id}
                    onClick={() => handleSelectConsultant(consultant)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '0.875rem',
                      borderRadius: '0.75rem',
                      border: `2px solid ${isSelected ? (consultant.color === 'orange' ? '#FB923C' : '#60A5FA') : '#E5E7EB'}`,
                      background: isSelected ? 
                        (consultant.color === 'orange' ? 'linear-gradient(to bottom right, #FEF3C7, #FED7AA)' : 'linear-gradient(to bottom right, #DBEAFE, #E0E7FF)') : 
                        'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                  >
                    {/* 이미지와 이름 */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{
                        width: '3.5rem',
                        height: '3.5rem',
                        borderRadius: '0.75rem',
                        background: consultant.color === 'orange' ? 
                          'linear-gradient(to bottom right, #FEF3C7, #FED7AA)' : 
                          'linear-gradient(to bottom right, #DBEAFE, #E0E7FF)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0.25rem',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        marginBottom: '0.5rem'
                      }}>
                        <img 
                          src={consultantImage} 
                          alt={consultant.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                      <h3 style={{ 
                        fontSize: '0.9375rem', 
                        fontWeight: '600', 
                        color: '#111827',
                        marginBottom: '0.125rem',
                        textAlign: 'center'
                      }}>
                        {consultant.name}
                      </h3>
                      <p style={{ 
                        fontSize: '0.6875rem', 
                        color: '#9CA3AF',
                        textAlign: 'center'
                      }}>
                        {consultant.title}
                      </p>
                    </div>

                    {/* 설명 */}
                    <p style={{ 
                      fontSize: '0.75rem', 
                      color: '#6B7280',
                      lineHeight: '1.3',
                      marginBottom: '0.75rem',
                      textAlign: 'center'
                    }}>
                      {consultant.description}
                    </p>

                    {/* 특징 */}
                    <div style={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                      marginBottom: '0.75rem'
                    }}>
                      {consultant.characteristics.slice(0, 2).map((char, idx) => (
                        <div key={idx} style={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          gap: '0.375rem',
                          fontSize: '0.6875rem',
                          color: '#4B5563'
                        }}>
                          <Star size={10} style={{ color: '#FCD34D', flexShrink: 0, marginTop: '0.125rem' }} />
                          <span style={{ lineHeight: '1.2' }}>{char}</span>
                        </div>
                      ))}
                    </div>

                    {/* 샘플 메시지 */}
                    <div style={{
                      padding: '0.5rem',
                      backgroundColor: '#F9FAFB',
                      borderRadius: '0.375rem',
                      border: '1px solid #E5E7EB'
                    }}>
                      <p style={{ 
                        fontSize: '0.625rem',
                        color: '#374151',
                        fontStyle: 'italic',
                        lineHeight: '1.3',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        "{consultant.sampleMessage}"
                      </p>
                    </div>

                    {/* 프롬프트 없음 표시 */}
                    {!consultant.systemPromptId && (
                      <div style={{
                        position: 'absolute',
                        top: '0.5rem',
                        left: '0.5rem',
                        backgroundColor: '#FEE2E2',
                        color: '#991B1B',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.625rem',
                        fontWeight: '600'
                      }}>
                        프롬프트 활성화 필요
                      </div>
                    )}
                    
                    {/* 선택 표시 */}
                    {isSelected && (
                      <div style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '1.25rem',
                        height: '1.25rem',
                        backgroundColor: '#10B981',
                        borderRadius: '50%'
                      }}>
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 데스크톱 스타일
  return (
    <div className="modal-overlay" style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999 
    }}>
      <div className="modal modal-lg" style={{ 
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        maxWidth: '52rem',
        padding: 0,
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)',
          padding: '1.5rem',
          borderRadius: '0.75rem 0.75rem 0 0',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                라떼이즈 컨설턴트를 선택해주세요
              </h2>
              <p style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                각 컨설턴트마다 고유한 코칭 스타일이 있습니다. 편안하게 느껴지는 스타일을 선택해주세요.
              </p>
            </div>
            <button
              onClick={onCancel}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '2.5rem',
                height: '2.5rem',
                minWidth: '2.5rem',
                minHeight: '2.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'background 0.2s',
                flexShrink: 0
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              <X size={20} color="white" />
            </button>
          </div>
        </div>

        {/* Consultant Cards */}
        <div style={{ padding: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            {consultants.map((consultant) => {
              const consultantImage = consultant.image;
              const isSelected = selectedConsultant?.id === consultant.id;
              const isHovered = hoveredConsultant === consultant.id;
              
              return (
                <button
                  key={consultant.id}
                  onClick={() => handleSelectConsultant(consultant)}
                  onMouseEnter={() => setHoveredConsultant(consultant.id)}
                  onMouseLeave={() => setHoveredConsultant(null)}
                  style={{
                    position: 'relative',
                    textAlign: 'left',
                    padding: '1.5rem',
                    borderRadius: '0.75rem',
                    border: `2px solid ${isSelected ? (consultant.color === 'orange' ? '#FB923C' : '#60A5FA') : '#E5E7EB'}`,
                    background: isSelected ? 
                      (consultant.color === 'orange' ? 'linear-gradient(to bottom right, #FEF3C7, #FED7AA)' : 'linear-gradient(to bottom right, #DBEAFE, #E0E7FF)') : 
                      'white',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    transform: isSelected ? 'scale(0.98)' : (isHovered ? 'translateY(-4px)' : 'none'),
                    boxShadow: isHovered && !isSelected ? '0 10px 25px rgba(0, 0, 0, 0.1)' : 'none',
                    width: '100%'
                  }}
                >
                  {/* Badge for personality
                  <div style={{
                    position: 'absolute',
                    top: '-0.75rem',
                    right: '1.5rem',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    background: consultant.color === 'orange' ? 
                      'linear-gradient(to right, #F59E0B, #EA580C)' : 
                      'linear-gradient(to right, #3B82F6, #6366F1)',
                    color: 'white'
                  }}>
                    {consultant.personality === 'charismatic' ? '베테랑' : '친절파'}
                  </div> */}

                  {/* Consultant Info */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{
                      width: '3rem',
                      height: '3rem',
                      borderRadius: '0.75rem',
                      background: consultant.color === 'orange' ? 
                        'linear-gradient(to bottom right, #FEF3C7, #FED7AA)' : 
                        'linear-gradient(to bottom right, #DBEAFE, #E0E7FF)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      padding: '0.25rem'
                    }}>
                      <img 
                        src={consultantImage} 
                        alt={consultant.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          filter: consultant.color === 'orange' ? 'none' : 'none'
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.25rem' }}>
                        {consultant.title}
                      </h3>
                      <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                        {consultant.description}
                      </p>
                    </div>
                  </div>

                  {/* Characteristics */}
                  <div style={{ marginBottom: '1rem' }}>
                    {consultant.characteristics.map((char, idx) => (
                      <div key={idx} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem', 
                        fontSize: '0.875rem', 
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}>
                        <Star size={12} style={{ color: '#FCD34D', flexShrink: 0 }} />
                        <span>{char}</span>
                      </div>
                    ))}
                  </div>

                  {/* Sample Message */}
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '0.5rem',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <MessageSquare size={14} style={{ color: '#9CA3AF' }} />
                      <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#6B7280' }}>
                        샘플 메시지
                      </span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#374151', fontStyle: 'italic' }}>
                      "{consultant.sampleMessage}"
                    </p>
                  </div>

                  {/* Selection Indicator */}
                  {isSelected && (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '0.75rem',
                      border: '2px solid #10B981',
                      pointerEvents: 'none'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '0.75rem',
                        left: '0.75rem',
                        backgroundColor: '#10B981',
                        color: 'white',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        선택됨
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantSelector;